<script setup lang="ts">
// Ports v3 `Skeletons/DashboardSkeleton.vue` one-to-one for the summary overview chart. v3's
// `SummaryDashboardChart` drew it in two halves around the live controls: `only-metric` (the
// metric-tile strip, inside `OrphexDashboardChart`) above the Group/Frequency row, and `only-dash`
// (the wavy trend box + legend, inside `OrphexLoadingText`'s `px-4 py-6` frame) below it. `part`
// picks the half. Each fill is the gray step the v3 class used (`Skeleton`'s ramp tones); v3's
// `rounded-lg`/`rounded` are this design system's `rounded-md`/`rounded-sm`.
import { Skeleton } from '@/shared/ui'

defineProps<{ part: 'metrics' | 'chart' }>()

const V3_RADIUS_LG = 'var(--ui-radius-md)'
const V3_RADIUS = 'var(--ui-radius-sm)'
</script>

<template>
  <div
    v-if="part === 'metrics'"
    class="overview-skeleton__metrics mb-4 flex flex-col md:flex-row md:items-start md:justify-between"
    aria-hidden="true"
  >
    <div class="mb-4 grid w-full grid-cols-1 gap-4 sm:grid-cols-2 md:mb-0 lg:grid-cols-5">
      <Skeleton
        v-for="tile in 4"
        :key="tile"
        class="overview-skeleton__tile"
        height="auto"
        :radius="V3_RADIUS_LG"
        tone="sunken"
      >
        <span class="flex items-center p-4">
          <span class="mr-2 block h-10 w-10 shrink-0 rounded-md bg-skeleton-medium" />
          <span class="block w-full">
            <span class="mb-2 block h-3 w-14 rounded-sm bg-skeleton-medium" />
            <span class="block h-4.5 w-26 rounded-sm bg-skeleton-medium" />
          </span>
        </span>
      </Skeleton>
    </div>
  </div>

  <div v-else class="overview-skeleton__chart w-full px-4 py-6" aria-hidden="true">
    <Skeleton height="auto" :radius="V3_RADIUS_LG" tone="sunken">
      <span class="block h-80 w-full md:h-96">
        <svg
          class="h-full w-full"
          preserveAspectRatio="none"
          fill="none"
          viewBox="0 0 900 350"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M0 108C88.298 108 131.789 44.3333 194.5 44.3333C257.211 44.3333 283.211 118.667 354 118.667C424.789 118.667 453.789 81.3333 521.5 81.3333C589.211 81.3333 618.702 143 707 143C795.298 143 821.5 96 890 96"
            stroke="currentColor"
            stroke-width="4"
            stroke-linecap="round"
            class="text-skeleton-medium"
          />
          <path
            d="M0 200C88.298 200 113.289 250.333 176 250.333C238.711 250.333 264.711 211.667 335.5 211.667C406.289 211.667 435.289 240.333 503 240.333C570.711 240.333 600.202 195 688.5 195C776.798 195 803 231 871.5 231"
            stroke="currentColor"
            stroke-width="4"
            stroke-linecap="round"
            class="text-skeleton-bold"
          />
        </svg>
      </span>
    </Skeleton>
    <div class="overview-skeleton__legend mt-6 flex items-center justify-center gap-8">
      <span v-for="item in 2" :key="item" class="flex items-center gap-2">
        <Skeleton width="1rem" height="0.25rem" radius="var(--ui-radius-full)" tone="medium" />
        <Skeleton width="4rem" height="1rem" :radius="V3_RADIUS" tone="medium" />
      </span>
    </div>
  </div>
</template>
